import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { selectAuthToken } from 'selectors';
import type { AxiosResponse } from 'axios';
import { useNotification } from 'hooks/useNotification';
import { integrationQueryKeys } from './integrationQueryKeys';
import { UpdateConnectionInfoParams, UpdateConnectionInfoResponse } from 'IntegrationModule/types';
import { updateConnectionInfoApi } from '../updateConnectionInfoApi';

export const useUpdateConnectionInfoMutation = () => {
  const token = useSelector(selectAuthToken);
  const { showSuccess, showError } = useNotification();
  const queryClient = useQueryClient();

  const mutation = useMutation<
    AxiosResponse<UpdateConnectionInfoResponse>,
    AxiosResponse<{ error: string; message: string; statusCode: number }>,
    UpdateConnectionInfoParams
  >({
    mutationFn: async (params) => {
      if (!token) throw new Error('Not Authenticated');
      return updateConnectionInfoApi({ params, token }) as Promise<
        AxiosResponse<UpdateConnectionInfoResponse>
      >;
    },
    onSuccess: () => {
      showSuccess('Connection info updated');
      queryClient.invalidateQueries({ queryKey: integrationQueryKeys.lists() });
    },
    onError: (error) => {
      showError(`Failed to update connection info: ${error?.data?.message || 'Unknown error'}`);
    },
  });

  return {
    updateConnectionInfo: mutation.mutate,
    updateConnectionInfoAsync: mutation.mutateAsync,
    isUpdatingConnectionInfo: mutation.isPending,
  };
};
